import React, { useEffect, useRef, useState } from "react";
import CommonModalView from "presentational/common/modals/CommonModalView";
import CommonListItemButtonView from "presentational/common/CommonListItemButtonView";
import ClearAdornmentButtonView from "presentational/common/ClearAdornmentButtonView";
import CommonTextField from "presentational/common/CommonTextField";
import CommonIconView from "presentational/common/CommonIconView";
import TXT from "presentational/common/multinational/TXT";
import Restful from "lib/restful";
import { withStore } from "store/storeHandler";
import config from "lib/fr-config";
import { getFilteredInput, replaceSpaceWithHyphen } from "lib/textFilter";
import { withStyles } from "@material-ui/core/styles";
import { withToken } from "lib/forceTokenRenewComponent";
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";

const transitionTime = config.css.animation.modalDurationMilliSec;

const colorCss = config.css.color;

const CustomTypography = withStyles({
  colorSecondary: { color: colorCss.colorOfDanger }
})(Typography);

const css = {
  writeBrandBtnWrapper: { marginTop: 40 }
};

BrandSelectorModalView.propTypes = {
  value: PropTypes.string,
  open: PropTypes.bool.isRequired,
  writeNewTagMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

BrandSelectorModalView.defaultProps = {
  writeNewTagMode: false
};

function BrandSelectorModalView(props) {
  const { value, open, writeNewTagMode, onClose, onSelect } = props;
  const { getIdTokenWT } = props;
  const { AppErrorActions } = props;
  const [mode, setMode] = useState("input");
  const [valueOfProps, setValueOfProps] = useState("");
  const [valueOfState, setValueOfState] = useState("");
  const [autoComplete, setAutoComplete] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const inputField = useRef();

  useEffect(() => {
    syncValue();
  });

  const syncValue = () => {
    if (value !== valueOfProps) {
      setValueOfProps(value);
      setValueOfState(value);
    }
  };

  const getFilteredBrandName = () => {
    return (
      valueOfState &&
      valueOfState !== "" &&
      getFilteredInput("brand", valueOfState)
    );
  };

  const getAutoComplete = async filteredText => {
    const brandName = replaceSpaceWithHyphen(filteredText);
    const { code, res, errInfo } = await Restful.getAutocompleteBrandListAPI({
      brandName
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    }
    return res;
  };

  const setNewBrandTagToServer = async brandTag => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;

    const { code, errInfo } = await Restful.setBrandTagAPI({
      idToken,
      data: { brandTag }
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    } else if (code === "UnauthorizedUser") {
      throw Error("Unauthorized Person Request New Brand Tag");
    }
  };

  const setBrandName = input => {
    setValueOfState(input);
  };

  const confirmBrandName = input => {
    setBrandName(input);
    onSelect(input);
  };

  const getBtnListElement = () => {
    if (!autoComplete) {
      console.log("error");
      console.error("error");
    }
    const { data, recommend } = autoComplete;

    const hasList =
      autoComplete &&
      ((data && data.length > 0) || (recommend && recommend.length > 0));
    const nullList = (
      <CustomTypography>
        <TXT name="msg_unknownBrand" />
      </CustomTypography>
    );
    const majorBrandList =
      data &&
      data.map(name => (
        <CommonListItemButtonView
          key={name}
          onClick={handleRecommendBtnClick(name)}
          color="highlighted"
          primary={name}
          dense
        />
      ));
    const minorBrandList =
      recommend &&
      recommend.map(name => (
        <CommonListItemButtonView
          key={name}
          onClick={handleRecommendBtnClick(name)}
          primary={name}
          dense
        />
      ));
    const addBrandTagBtn = (
      <CommonListItemButtonView
        color="highlighted"
        onClick={handleAddBrandBtnClick}
        primary={getFilteredBrandName()}
        icon={<CommonIconView type={"add"} fontSize={"small"} />}
        dense
      />
    );

    return (
      <div>
        {hasList ? (
          <div>
            <div>{majorBrandList}</div>
            <div>{minorBrandList}</div>
          </div>
        ) : (
          nullList
        )}
        {writeNewTagMode && (
          <div style={css.writeBrandBtnWrapper}>{addBrandTagBtn}</div>
        )}
      </div>
    );
  };

  const closeModal = () => {
    onClose();

    setTimeout(function () {
      setAutoComplete({});
      setMode("input");
    }, transitionTime);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleNextClick();
      e.preventDefault();
    }
  };

  const handleChange = e => {
    const { value } = e.target;
    setBrandName(value);
  };

  const handleNextClick = async () => {
    const filteredBrandName = getFilteredBrandName();

    if (!filteredBrandName || filteredBrandName === "") return null;

    let autoComplete;
    try {
      autoComplete = await getAutoComplete(filteredBrandName);
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
      return null;
    }

    setAutoComplete(autoComplete);
    const { data } = autoComplete;
    if (data.length === 1) {
      confirmBrandName(data[0]);
      closeModal();
    } else {
      setMode("select");
    }
  };

  const handleBackClick = () => {
    setMode("input");
  };

  const handleRecommendBtnClick = value => () => {
    confirmBrandName(value);
    closeModal();
  };

  const handleAddBrandBtnClick = async () => {
    const brandName = getFilteredBrandName();

    if (!brandName || brandName === "") return null;

    if (isUploading) return null;

    await setIsUploading(true);

    try {
      await setNewBrandTagToServer(brandName);
    } catch (error) {
      setIsUploading(false);
      AppErrorActions.setSafeAppError(error);
      closeModal();
      return null;
    }

    setIsUploading(false);
    confirmBrandName(brandName);
    closeModal();
  };

  const handleReset = () => {
    setBrandName("");
    inputField.current.focus();
  };

  const handleClose = () => {
    closeModal();
  };

  const render = () => {
    const fstBtnText =
      mode === "input" ? (
        <TXT name="btnModal_cancel" />
      ) : (
        <TXT name="btnModal_back" />
      );
    const fstBtnFunc = mode === "input" ? handleClose : handleBackClick;
    const secBtnText = mode === "input" ? <TXT name="btnModal_next" /> : null;
    const secBtnFunc = mode === "input" ? handleNextClick : null;
    const inputGuideText = <TXT name="msg_helperBrandInputGuide" />;

    return (
      <CommonModalView
        open={open}
        onClose={handleClose}
        paperStyle={{ width: "100%" }}
        titleText={<TXT name="titleModal_brand" />}
        contentComponent={
          <div>
            {mode === "input" && (
              <CommonTextField
                inputRef={inputField}
                autoFocus
                value={valueOfState || ""}
                onKeyDown={handleKeyPress}
                onChange={handleChange}
                margin="normal"
                helperText={getFilteredBrandName() || inputGuideText}
                InputProps={{
                  endAdornment: (
                    <ClearAdornmentButtonView onClick={handleReset} />
                  )
                }}
              />
            )}
            {mode === "select" && getBtnListElement()}
          </div>
        }
        firstButtonText={fstBtnText}
        firstButtonFunc={fstBtnFunc}
        secondButtonText={secBtnText}
        secondButtonFunc={secBtnFunc}
        secondButtonDisabled={mode === "input" && !getFilteredBrandName()}
      />
    );
  };

  return render();
}

export default withToken(
  withStore({
    dispatchMap: ["appError"]
  })(BrandSelectorModalView)
);
